/**
 * Les commandes de lecture, communes à toute vue animée.
 *
 * Elles étaient écrites dans la vue de la chute. La troisième expérience —
 * l'orbite — en réclamait les mêmes, et recopier une boucle
 * `requestAnimationFrame` est le genre de duplication qui se paie deux fois :
 * une fois à l'écriture, une fois le jour où l'une des deux copies corrige un
 * défaut que l'autre garde.
 *
 * ## Ce que ce module tient, et qui n'est pas négociable
 *
 * **Le temps est une entrée, jamais une horloge.** Ce module avance un
 * compteur de secondes et appelle `render(elapsed)` ; c'est la vue qui va
 * chercher l'état à cet instant-là, auprès d'une fonction pure du noyau. Rien
 * n'est accumulé d'une image à l'autre. Deux conséquences, et ce sont elles
 * qui justifient qu'une animation existe dans ce projet (CLAUDE.md §5) :
 *
 * - une machine lente montre le même mouvement, moins fluide ;
 * - reculer le curseur rejoue exactement les mêmes positions.
 *
 * L'horloge du navigateur ne décide que du **moment où l'on regarde**, ce qui
 * est un choix d'affichage et pas un calcul.
 */

import { escape, escapeAttribute, formatNumber } from "./format.js";
import { fr } from "../i18n/fr.js";

/** Pas du curseur de temps, en secondes. Assez fin pour viser un instant précis. */
const TIME_STEP = 0.01;

/**
 * Fraction du mouvement montrée d'emblée quand l'animation est désactivée.
 *
 * Ni zéro — un mouvement à son point de départ n'apprend rien — ni un, où tout
 * est terminé. Au milieu, l'écart entre les modèles est déjà visible : c'est
 * l'image qui dit le plus à qui ne verra jamais qu'elle.
 */
const STILL_FRACTION = 0.6;

export interface Player {
  /** À appeler quand les réglages ont changé : réaffiche l'instant courant. */
  readonly redraw: () => void;
  readonly detach: () => void;
}

export function renderPlayerControls(id: string): string {
  const timeId = `verlet-player-time-${id}`;
  return [
    `<div class="verlet-player">`,
    `<button type="button" class="verlet-toggle" data-player-play`,
    ` aria-pressed="false">${escape(fr.fallPlay)}</button>`,
    `<button type="button" class="verlet-toggle" data-player-replay>${escape(fr.fallReplay)}</button>`,
    `<label class="verlet-player__time" for="${escapeAttribute(timeId)}">`,
    `${escape(fr.fallTimeLabel)}</label>`,
    `<input type="range" id="${escapeAttribute(timeId)}" data-player-time min="0" max="1"`,
    ` step="${TIME_STEP}" value="0" aria-valuetext="0,00 s" />`,
    `<output for="${escapeAttribute(timeId)}" data-player-readout>0,00 s</output>`,
    `</div>`,
  ].join("");
}

/**
 * Branche les commandes et rend la boucle.
 *
 * `render` reçoit l'instant à afficher et ne rend rien : c'est la vue qui sait
 * quoi en faire. Ce module ne connaît ni bille, ni orbite.
 */
export function bindPlayer(
  root: ParentNode,
  duration: () => number,
  render: (elapsed: number) => void,
): Player {
  const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let elapsed = motion.matches ? duration() * STILL_FRACTION : 0;
  let playing = !motion.matches;

  const playButton = root.querySelector<HTMLButtonElement>("[data-player-play]");
  const replayButton = root.querySelector<HTMLButtonElement>("[data-player-replay]");
  const timeInput = root.querySelector<HTMLInputElement>("[data-player-time]");
  const timeReadout = root.querySelector<HTMLElement>("[data-player-readout]");

  const syncControls = (): void => {
    if (timeInput !== null) {
      timeInput.max = String(duration());
      const value = String(Math.round(elapsed / TIME_STEP) * TIME_STEP);
      if (timeInput.value !== value) timeInput.value = value;
      timeInput.setAttribute("aria-valuetext", `${formatNumber(elapsed, 2)} s`);
    }
    if (timeReadout !== null) timeReadout.textContent = `${formatNumber(elapsed, 2)} s`;
    if (playButton !== null) {
      playButton.textContent = playing ? fr.fallPause : fr.fallPlay;
      playButton.setAttribute("aria-pressed", playing ? "true" : "false");
    }
  };

  let previous: number | undefined;
  let frameHandle = 0;
  let detached = false;
  const listeners = new AbortController();

  const tick = (now: number): void => {
    if (detached) return;
    frameHandle = window.requestAnimationFrame(tick);
    if (!playing) {
      previous = now;
      return;
    }

    // Le pas est borné : un onglet resté en arrière-plan rendrait sinon un
    // saut de plusieurs secondes au retour, et le mouvement serait fini avant
    // d'avoir commencé.
    const delta = previous === undefined ? 0 : Math.min((now - previous) / 1000, 0.1);
    previous = now;
    elapsed += delta;

    const total = duration();
    if (elapsed >= total) {
      elapsed = total;
      playing = false;
    }
    syncControls();
    render(elapsed);
  };

  frameHandle = window.requestAnimationFrame(tick);

  playButton?.addEventListener("click", () => {
    // Rejouer plutôt que repartir de la fin : appuyer sur « lecture » quand
    // tout est terminé ne doit pas ne rien faire.
    if (!playing && elapsed >= duration()) elapsed = 0;
    playing = !playing;
    previous = undefined;
    syncControls();
  }, { signal: listeners.signal });

  replayButton?.addEventListener("click", () => {
    elapsed = 0;
    playing = true;
    previous = undefined;
    syncControls();
    render(elapsed);
  }, { signal: listeners.signal });

  timeInput?.addEventListener("input", () => {
    const value = Number.parseFloat(timeInput.value);
    if (!Number.isFinite(value)) return;
    elapsed = value;
    playing = false;
    previous = undefined;
    syncControls();
    render(elapsed);
  }, { signal: listeners.signal });

  const onMotionChange = (event: MediaQueryListEvent): void => {
    if (!event.matches) return;
    elapsed = duration() * STILL_FRACTION;
    playing = false;
    previous = undefined;
    syncControls();
    render(elapsed);
  };
  motion.addEventListener("change", onMotionChange);

  return {
    redraw: () => {
      if (detached) return;
      // Un réglage a changé : on reste à l'instant courant plutôt que de
      // repartir de zéro. C'est délibéré — à instant figé, pousser un curseur
      // montre ce qu'il change et ce qu'il ne change pas. Relancer l'effacerait.
      elapsed = Math.min(elapsed, duration());
      syncControls();
      render(elapsed);
    },
    detach: () => {
      if (detached) return;
      detached = true;
      listeners.abort();
      motion.removeEventListener("change", onMotionChange);
      window.cancelAnimationFrame(frameHandle);
    },
  };
}
